/**
 * UiProvider - Frontend UI Context Provider
 *
 * Populates FrontendThemeContext with the frontend-specific variants (BannerSlider, Stars, etc.).
 * The shared UI primitives take their classes from the wrappers in ../../components/ui, not from a context.
 *
 * Adding a new frontend variant requires changes ONLY in Frontend/Theme:
 * - Add config in configs/
 * - Add the interface and hook in hooks/useFrontendVariants.ts, and the values to this provider
 * No Infrastructure changes needed.
 */

import { createContext, useContext } from 'react'

import * as BannerSliderConfig from '../../configs/bannerSlider.config'
import * as CategoryCardsConfig from '../../configs/categoryCards.config'
import * as HomepageConfig from '../../configs/homepage.config'
import * as PriceInfoConfig from '../../configs/priceInfo.config'
import * as ProductSliderConfig from '../../configs/productSlider.config'
import * as PromoBlockConfig from '../../configs/promoBlock.config'
import * as ReviewsCarouselConfig from '../../configs/reviewsCarousel.config'
import * as SkeletonConfig from '../../configs/skeleton.config'
import * as StarsConfig from '../../configs/stars.config'

import type {
  BannerSliderVariants,
  CategoryCardsVariants,
  HomepageVariants,
  PriceInfoVariants,
  ProductSliderVariants,
  ProductViewSkeletonVariants,
  PromoBlockVariants,
  ReviewsCarouselVariants,
  StarsVariants,
} from '../../hooks/useFrontendVariants'

// =============================================================================
// FRONTEND THEME CONTEXT (frontend-specific variants)
// =============================================================================

/**
 * Frontend-specific theme context value: every variant group of hooks/useFrontendVariants.ts
 *
 * All fields are required - no optional checks needed in hooks.
 */
export type FrontendThemeContextValue = ProductViewSkeletonVariants &
  StarsVariants &
  BannerSliderVariants &
  PriceInfoVariants &
  ProductSliderVariants &
  PromoBlockVariants &
  CategoryCardsVariants &
  HomepageVariants &
  ReviewsCarouselVariants

export const FrontendThemeContext = createContext<FrontendThemeContextValue | null>(null)

// =============================================================================
// PROVIDER
// =============================================================================

export interface UiProviderProps {
  children: React.ReactNode
}

/**
 * UiProvider - Provides the frontend-specific variants through FrontendThemeContext
 */
export function UiProvider({ children }: UiProviderProps) {
  // Frontend-specific theme config
  const frontendConfig: FrontendThemeContextValue = {
    // ProductView Skeleton
    skeletonSectionVariants: SkeletonConfig.skeletonSectionVariants,
    skeletonSliderGridVariants: SkeletonConfig.skeletonSliderGridVariants,
    skeletonSliderItemVariants: SkeletonConfig.skeletonSliderItemVariants,
    skeletonDetailsRowVariants: SkeletonConfig.skeletonDetailsRowVariants,
    skeletonContentAreaVariants: SkeletonConfig.skeletonContentAreaVariants,

    // Stars
    starsContainerVariants: StarsConfig.starsContainerVariants,
    starIconVariants: StarsConfig.starIconVariants,

    // Banner Slider
    bannerSectionVariants: BannerSliderConfig.bannerSectionVariants,
    bannerItemVariants: BannerSliderConfig.bannerItemVariants,
    bannerLinkVariants: BannerSliderConfig.bannerLinkVariants,
    bannerImageVariants: BannerSliderConfig.bannerImageVariants,
    bannerFallbackVariants: BannerSliderConfig.bannerFallbackVariants,
    bannerFallbackContentVariants: BannerSliderConfig.bannerFallbackContentVariants,
    bannerFallbackTitleVariants: BannerSliderConfig.bannerFallbackTitleVariants,
    bannerFallbackCtaVariants: BannerSliderConfig.bannerFallbackCtaVariants,
    bannerNavButtonVariants: BannerSliderConfig.bannerNavButtonVariants,
    bannerNavIconVariants: BannerSliderConfig.bannerNavIconVariants,
    bannerSwiperVariants: BannerSliderConfig.bannerSwiperVariants,
    bannerSkeletonVariants: BannerSliderConfig.bannerSkeletonVariants,
    swiperPaginationBullet: BannerSliderConfig.SWIPER_PAGINATION_BULLET,
    swiperPaginationBulletActive: BannerSliderConfig.SWIPER_PAGINATION_BULLET_ACTIVE,

    // Price Info (Product View)
    priceInfoWrapperVariants: PriceInfoConfig.priceInfoWrapperVariants,
    priceInfoStrikethroughVariants: PriceInfoConfig.priceInfoStrikethroughVariants,
    priceInfoFinalPriceVariants: PriceInfoConfig.priceInfoFinalPriceVariants,
    priceInfoVatVariants: PriceInfoConfig.priceInfoVatVariants,
    priceInfoMsrpVariants: PriceInfoConfig.priceInfoMsrpVariants,
    priceInfoTooltipVariants: PriceInfoConfig.priceInfoTooltipVariants,

    // Product Slider
    productSliderSectionVariants: ProductSliderConfig.productSliderSectionVariants,
    productSliderTitleVariants: ProductSliderConfig.productSliderTitleVariants,
    productSliderWrapperVariants: ProductSliderConfig.productSliderWrapperVariants,
    productSliderSwiperVariants: ProductSliderConfig.productSliderSwiperVariants,
    productSliderSlideVariants: ProductSliderConfig.productSliderSlideVariants,
    productSliderSkeletonGridVariants: ProductSliderConfig.productSliderSkeletonGridVariants,
    productSliderGridVariants: ProductSliderConfig.productSliderGridVariants,
    productSliderGridItemVariants: ProductSliderConfig.productSliderGridItemVariants,

    // Promo Block
    promoBlockSectionVariants: PromoBlockConfig.promoBlockSectionVariants,
    promoBlockContainerVariants: PromoBlockConfig.promoBlockContainerVariants,
    promoBlockImageSectionVariants: PromoBlockConfig.promoBlockImageSectionVariants,
    promoBlockImageLinkVariants: PromoBlockConfig.promoBlockImageLinkVariants,
    promoBlockImageVariants: PromoBlockConfig.promoBlockImageVariants,
    promoBlockContentSectionVariants: PromoBlockConfig.promoBlockContentSectionVariants,
    promoBlockContentInnerVariants: PromoBlockConfig.promoBlockContentInnerVariants,
    promoBlockTitleVariants: PromoBlockConfig.promoBlockTitleVariants,
    promoBlockDescriptionVariants: PromoBlockConfig.promoBlockDescriptionVariants,
    promoBlockSkeletonContentVariants: PromoBlockConfig.promoBlockSkeletonContentVariants,
    promoBlockSkeletonInnerVariants: PromoBlockConfig.promoBlockSkeletonInnerVariants,

    // Category Cards
    categoryCardsGridVariants: CategoryCardsConfig.categoryCardsGridVariants,
    categoryCardItemVariants: CategoryCardsConfig.categoryCardItemVariants,
    categoryCardImageVariants: CategoryCardsConfig.categoryCardImageVariants,
    categoryCardTitleVariants: CategoryCardsConfig.categoryCardTitleVariants,
    categoryCardSkeletonVariants: CategoryCardsConfig.categoryCardSkeletonVariants,

    // Homepage Sections
    homepageSectionVariants: HomepageConfig.homepageSectionVariants,

    // Reviews Carousel
    reviewsCarouselSectionVariants: ReviewsCarouselConfig.reviewsCarouselSectionVariants,
    reviewsCarouselSummaryVariants: ReviewsCarouselConfig.reviewsCarouselSummaryVariants,
    reviewsCarouselAverageVariants: ReviewsCarouselConfig.reviewsCarouselAverageVariants,
    reviewsCarouselSwiperVariants: ReviewsCarouselConfig.reviewsCarouselSwiperVariants,
    reviewsCarouselSlideVariants: ReviewsCarouselConfig.reviewsCarouselSlideVariants,
    reviewCardVariants: ReviewsCarouselConfig.reviewCardVariants,
    reviewCardNicknameVariants: ReviewsCarouselConfig.reviewCardNicknameVariants,
    reviewCardSummaryVariants: ReviewsCarouselConfig.reviewCardSummaryVariants,
    reviewCardBodyVariants: ReviewsCarouselConfig.reviewCardBodyVariants,
    reviewCardDateVariants: ReviewsCarouselConfig.reviewCardDateVariants,
    reviewCardProductImageVariants: ReviewsCarouselConfig.reviewCardProductImageVariants,
    reviewCardProductNameVariants: ReviewsCarouselConfig.reviewCardProductNameVariants,
    reviewsCarouselSkeletonGridVariants: ReviewsCarouselConfig.reviewsCarouselSkeletonGridVariants,
    reviewsCarouselSkeletonItemVariants: ReviewsCarouselConfig.reviewsCarouselSkeletonItemVariants,
  }

  return (
    <FrontendThemeContext value={frontendConfig}>
      {children}
    </FrontendThemeContext>
  )
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to access frontend-specific theme variants
 * @throws Error if used outside Frontend UiProvider
 */
export function useFrontendTheme(): FrontendThemeContextValue {
  const context = useContext(FrontendThemeContext)
  if (!context) {
    throw new Error('useFrontendTheme must be used within Frontend UiProvider')
  }
  return context
}
